import MessageIndex from "./channel_index/MessageIndex.js";

import Dexie        from "dexie";
import global       from "@/prototype/global.js";
import uuid         from "@/library/uuid.js";
import Error        from "@/prototype/utils/Error.js";
import WeakIterable from "@/prototype/utils/WeakIterable.js";

const log = global.log.getLogger("f_mtac.storage.space.channel");

const CHANNEL_DATABASE_VERSION = 1;

const data_store_name = ({space_index, channel_index}) => `channel/${space_index}/${channel_index}`;
const database_name = ({space_index, channel_index}) => `f_mtac.channel.${space_index}.${channel_index}`;

const Channel = function(space, channel_index) {
    var database = new Dexie(`f_mtac.channel.${space.index}.${channel_index}`);
    var message_index = new MessageIndex(this);
    database.version(CHANNEL_DATABASE_VERSION).stores({
        messages: "local_id, rx_time, tx_time",
    });
    var room = null;
    Object.defineProperty(this, "space", {
        set: () => {},
        get: () => space
    });
    Object.defineProperty(this, "room_id", {
        set: () => {},
        get: () => (room !== null) ? room.room_id : null
    });
    Object.defineProperty(this, "room", {
        set: () => {},
        get: () => room
    });
    Object.defineProperty(this, "index", {
        set: () => {},
        get: () => channel_index
    });
    this.init = (room) => message_index.init(room);
    this.deinit = () => message_index.deinit();

    this.message_create = function({sender_name, sender_login, text, type, ack, sender_tx_time, media_id, tx_time, rx_time}) {
        log.info("message create ",text);
        var new_message = {}, changeset;
        new_message.space_index = space.index;
        new_message.local_id = `local_message-${uuid()}`;
        if (ack !== undefined) {
            new_message.ack = ack;
        } else {
            new_message.ack = 0;
        }
        if (text !== undefined) {
            new_message.text = text;
        }
        if (type !== undefined) {
            new_message.type = type;
        }
        if (sender_tx_time !== undefined) {
            new_message.sender_tx_time = sender_tx_time;
        }
        if (media_id !== undefined){
            new_message.media_id = null;
        }
        if (tx_time !== undefined) {
            new_message.tx_time = tx_time;
        } else {
            new_message.tx_time = Date.now() * 1000;
        }
        if (rx_time !== undefined) {
            new_message.rx_time = rx_time;
        }
        if (sender_name !== undefined) {
            new_message.sender_name = sender_name;
        }
        if (sender_login !== undefined) {
            new_message.sender_login = sender_login;
            if (sender_login === global.configuration.user.login){
                new_message.ack = 1;
            }
        }
        changeset = message_index.commit({
            local_id: new_message.local_id,
            rx_time: new_message.rx_time,
            tx_time: new_message.tx_time,
            new_message
        });
        if (changeset !== null) {
            new_message.tx_time = changeset.tx_time;
        }
        return database.transaction("rw", database.messages, () => {
            return database.messages.put(new_message); 
        }).catch(error => {
            log.error("message create error: ", error);
            throw error;
        }).then(() => {
            global.event_bus.publish("storage.channel.message.created", {});
            if (rx_time === undefined) {
                return message_index.push().catch(error => {
                    switch(error.status){
                    case Error.status.netdown:
                    case Error.status.hostdown:
                        return;
                    default:
                        throw error;
                    }
                });
            }
        }).then(() => new_message);
    };

    this.message_retrieve = function({rx_time}) {
        var messages = database.messages;
        if (rx_time !== undefined) {
            messages = messages.where("rx_time").equals(rx_time);
        }
        if (rx_time === undefined) {
            messages = messages.toCollection();
        }
        return messages;
    };

    this.message_delete_all = function({rx_time}) {
        log.info("message delete all rx_time = ",rx_time);
        message_index.commit_delete_all({rx_time});
        return database.messages.toCollection().modify((old_message, ref) => {
            if (rx_time === undefined || !("rx_time" in old_message) || rx_time === 0 || old_message.rx_time < rx_time) {
                delete ref.value;
            }
        }).catch(error => {
            log.error("message delete error:", error);
            throw error;
        }).then(() => {
            global.event_bus.publish("storage.channel.message.deleted", {});
            if (rx_time === undefined) {
                return message_index.push();
            }
        }); 
    };

    this.message_notif_delete_all = function() {
        log.error("this fonction is deprecated, use message_delete_all !!!!");
        return this.message_delete_all({rx_time: 0});
    };

    this.message_empty = function() {
        log.error("this fonction is deprecated, use message_delete_all !!!!");
        return this.message_delete_all({rx_time: 0});
    };

    this.message_delete = function({local_id}) {
        let messages = database.messages;
        if (local_id !== undefined) {
            messages = messages.where("local_id").equals(local_id);
        }
        return database.transaction("rw", database.messages, () => {
            return messages.modify((_, ref) => {
                delete ref.value;
            });
        }).catch(error => {
            if ("failedKeys" in error) {
                log.error("message update error: ", error, " when was updating keys ", error.failedKeys);
                error.failures.forEach((failure, index) => {
                    log.error(`object update failure ${index}: `, failure);
                });
            } else {
                log.error("message update error: ", error);
            }
            throw error;
        }).then((modified) => {
            if (modified === 0) {
                return;
            }
            global.event_bus.publish("storage.channel.message.deleted", {});
        });
    };

    // user for local updates only
    this.message_update = function({local_id, ack, rx_time, tx_time}){
        let messages = database.messages;
        if (local_id !== undefined) {
            messages = messages.where("local_id").equals(local_id);
        }
        return database.transaction("rw", database.messages, () => {
            return messages.modify(message => {
                if (ack !== undefined) {
                    message.ack = ack;
                }
                if (rx_time !== undefined) {
                    message.rx_time = rx_time;
                }
                if (tx_time !== undefined) {
                    message.tx_time = tx_time;
                }
            });
        }).catch(error => {
            if ("failedKeys" in error) {
                log.error("message update error: ", error, " when was updating keys ", error.failedKeys);
                error.failures.forEach((failure, index) => {
                    log.error(`object update failure ${index}: `, failure);
                });
            } else {
                log.error("message update error: ", error);
            }
            throw error;
        }).then((modified) => {
            if (modified === 0) {
                return;
            }
            global.event_bus.publish("storage.channel.message.updated", {});
        });
    };

    this.message_ack_update = function({local_id}){
        log.error("this fonction is deprecated, use message_update !!!!");
        return database.messages.update({local_id, ack: false});
    };

    let message_delete_handler = new WeakIterable();
    let message_create_handler = new WeakIterable();
    let message_update_handler = new WeakIterable();
    let message_delete_hook = (primary_key, message, transaction) => {
        primary_key; transaction;
        for (let handler of message_delete_handler) {
            handler(message);
        }
    };
    let message_create_hook = (primary_key, message, transaction) => {
        primary_key; transaction;
        for (let handler of message_create_handler) {
            handler(message);
        }
    };
    let message_update_hook = (modifications, primary_key, message, transaction) => {
        primary_key; transaction;
        for (let handler of message_update_handler) {
            handler(message, modifications);
        }
    };
    database.messages.hook("deleting", message_delete_hook);
    database.messages.hook("creating", message_create_hook);
    database.messages.hook("updating", message_update_hook);
    this.message_on = function(event_name, handler) {
        switch (event_name) {
        case "deleting":
            message_delete_handler.add(handler);
            break;
        case "creating":
            message_create_handler.add(handler);
            break;
        case "updating":
            message_update_handler.add(handler);
            break;
        default:
            throw "unsupported message subscribe event";
        }
    };
    this.message_off = function(event_name, handler) {
        switch (event_name) {
        case "deleting":
            message_delete_handler.delete(handler);
            break;
        case "creating":
            message_create_handler.delete(handler);
            break;
        case "updating":
            message_update_handler.delete(handler);
            break;
        default:
            throw "unsupported message unsubscribe event";
        }
    };
    this.delete = function(){
        database.messages.hook("deleting").unsubscribe(message_delete_hook);
        database.messages.hook("creating").unsubscribe(message_create_hook);
        database.messages.hook("updating").unsubscribe(message_update_hook);
        return database.delete();
    };

    this.duplicate = async function(new_space_index, new_channel_index) {
        if (this.space.index === new_space_index && this.index === new_channel_index) {
            return;
        }
        const new_database = new Dexie(Channel.database_name({space_index: new_space_index, channel_index: new_channel_index}));
        const schema = database.tables.reduce((result, table)=>{
            result[table.name] = ([table.schema.primKey].concat(table.schema.indexes).map(indexSpec => indexSpec.src)).toString();
            return result;
        }, {});
        new_database.version(database.verno).stores(schema);

        await database.tables.reduce(
            (prev, table) => {
                return prev.then(() => table.toArray())
                    .then(rows => new_database.table(table.name).bulkPut(rows));
            },
            Promise.resolve()
        );
        await new_database.close();
    };

    this.is_empty = async () => {
        var messages = database.messages.toCollection();
        let result = false;
        await messages.count().then((count) => {
            if (count === 0) {
                result = true;
            }
        });
        return result;
    };

    this.pull = () => message_index.pull();
    this.push = () => message_index.push();
};
Channel.data_store_name = data_store_name;
Channel.database_name = database_name;

export default Channel;
